import { getApiErrorMessage } from "@/lib/apiError";
import { notifyError, notifySuccess } from "@/lib/notification";
import * as interviewService from "@/services/interview.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { internshipsKey } from "./internship.queries";

export const interviewsKey = {
  all: ["interviews"] as const,
  byApplication: (applicationId: string) =>
    [...interviewsKey.all, "application", applicationId] as const,
};

export const useApplicationInterviewsQuery = (
  applicationId: string,
  enabled = true
) =>
  useQuery({
    queryKey: interviewsKey.byApplication(applicationId),
    queryFn: () => interviewService.fetchApplicationInterviews(applicationId),
    enabled: enabled && !!applicationId,
  });

export const useCreateInterviewMutation = (internshipId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: interviewService.InterviewCreateType;
    }) => interviewService.createInterview(applicationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: interviewsKey.byApplication(variables.applicationId),
      });
      // The backend flips the application to `interview_scheduled` itself,
      // so refetch the list rather than patching the status from here.
      queryClient.invalidateQueries({
        queryKey: [...internshipsKey.detail(internshipId), "applications"],
      });
      notifySuccess(
        "Interview scheduled",
        "The student has been notified by email."
      );
    },
    onError: (error: any) =>
      notifyError(getApiErrorMessage(error, "Failed to schedule interview")),
  });
};

/**
 * Handles reschedule, cancel, mark-completed and the feedback/rating save.
 * `successMessage` lets the caller phrase the toast for the action taken.
 */
export const useUpdateInterviewMutation = (
  internshipId: string,
  applicationId?: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      interviewId,
      data,
    }: {
      interviewId: string;
      data: interviewService.InterviewUpdateType;
      successMessage?: string;
      successDescription?: string;
    }) => interviewService.updateInterview(interviewId, data),
    onSuccess: (_, variables) => {
      if (applicationId) {
        queryClient.invalidateQueries({
          queryKey: interviewsKey.byApplication(applicationId),
        });
      } else {
        queryClient.invalidateQueries({ queryKey: interviewsKey.all });
      }
      // `completed` moves the application to `interviewed`, so the
      // applications table has to be refetched too.
      queryClient.invalidateQueries({
        queryKey: [...internshipsKey.detail(internshipId), "applications"],
      });
      notifySuccess(
        variables.successMessage || "Interview updated",
        variables.successDescription
      );
    },
    onError: (error: any) =>
      notifyError(getApiErrorMessage(error, "Failed to update interview")),
  });
};
